import { useQuery } from '@tanstack/react-query';
import { queryKeys } from './query-keys';
import { MediaEntry } from '../../types';
import { fetchWithBypass } from '../../utils/fetch';

interface UseMediaEntriesQueryOptions {
  enabled?: boolean;
}

/**
 * React Query hook to fetch the multi-entry media lines of a valuation
 * (valuation_media_entries table, see docs/MEDIA_VALUE_MULTI_ENTRY_PLAN.md).
 *
 * Returns [] for legacy valuations that only have summary values.
 */
export function useMediaEntriesQuery(
  valuationId: string | undefined,
  organizationId: string | undefined,
  options?: UseMediaEntriesQueryOptions
) {
  return useQuery({
    queryKey: queryKeys.valuations.mediaEntries(valuationId || ''),
    queryFn: async () => {
      if (!valuationId || !organizationId) {
        throw new Error('Valuation ID and organization ID are required');
      }

      const response = await fetchWithBypass(
        `/api/products/valuations/media-entries?valuationId=${valuationId}&organizationId=${organizationId}`
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch media entries');
      }

      const data = await response.json();
      return (data.entries || []) as MediaEntry[];
    },
    enabled: options?.enabled !== false && !!valuationId && !!organizationId,
    staleTime: 0, // Always refetch after cache invalidation
    gcTime: 30 * 60 * 1000,
    refetchOnMount: 'always',
  });
}
